import React, { ReactElement, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FormProvider, useForm, useWatch } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { EnterpriseGbtfSite, Language, Supervisor, SupervisorWithSitesForm } from '../../app/types'
import ErrorAlert from '../../common/components/ErrorAlert'
import FormContainer from '../../common/form/FormContainer'
import FormRow from '../../common/form/FormRow'
import FormFieldBox from '../../common/form/FormFieldBox'
import TextInput from '../../common/form/TextInput'
import { useFormSubmission } from '../../common/servercall/useFormSubmission'
import FormGroup from '../../common/form/FormGroup'
import { getAutocompleteUsers } from '../../common/util/helpers'
import { useSelector } from 'react-redux'
import { meSelector, selectedSiteSelector } from '../../authentication/redux'
import { api, buildPaginatedUrl } from '../../common/api'
import FormCheckbox from '../../common/form/FormCheckbox'

const schema = yup
    .object()
    .shape({
        supervisorUser: yup.object().required("required-m").nullable(),
        superviseAllSites: yup.boolean().required(),
        supervisedSiteIds: yup.array().when(['superviseAllSites'], {
            is: (superviseAllSites: boolean) => !superviseAllSites,
            then: schema => yup.array().compact(function (v) {
                return v == null;
            }).min(1, 'supervised-site-min-1-required'),
            otherwise: yup.array().nullable()
        })
    }).required()

type Props = {
    supervisorForm: SupervisorWithSitesForm
    onSubmit: (formData: SupervisorWithSitesForm) => void
    buildFormButtons: (isSubmitting: boolean) => ReactElement
}

export const mapSupervisorToForm = (supervisor: Supervisor) : SupervisorWithSitesForm => ({
    superviseAllSites: supervisor.superviseAllSites,
    supervisedSiteIds: supervisor.supervisedSites.map(site => site.siteId),
    supervisorUser: !supervisor.userId ? null : {
        uuid: supervisor.userId,
        firstName: supervisor.userFirstName || '',
        lastName: supervisor.userLastName || '',
        number: null,
        language: Language.en
    }
})

const AmccSupervisorFormScreen = ({supervisorForm, onSubmit, buildFormButtons }: Props) => {
    const { t } = useTranslation()
    const site = useSelector(selectedSiteSelector)
    const me = useSelector(meSelector)
    const [sites, setSites] = useState<EnterpriseGbtfSite[]>()

    useEffect(() => {
            const fetch = async () => {
                try {
                    if (!me || !site ) return
                    const { data } = await api.get(buildPaginatedUrl(`/api/enterprise/${me.voiceCloudProductId}/sites`, 0,  {field: 'name', order: 'asc'}, null, 1000))
                    setSites(data.content)
                } catch (err: any) {

                }
            }
            fetch();
        },
        [me, site]
    )

    const siteOptions = useMemo(
        () => !!sites ? sites.map(site => ({
            value: site?.id,
            label: site?.name,
        })) : [],
        [sites]
    )

    const { control, handleSubmit, setError, clearErrors, setValue, getFieldState } = useForm<SupervisorWithSitesForm>({
        resolver: yupResolver(schema),
        values: {...supervisorForm,
            supervisedSiteIds: sites?.map(site => supervisorForm.supervisedSiteIds.includes(site.id)? site.id : null) || []
        },
    })

    const { submit, isSubmitting, serverError, onError } = useFormSubmission<SupervisorWithSitesForm>(onSubmit, setError)

    const supervisorOptionParams = useMemo(() => {
        return getAutocompleteUsers(t('flows.edit.no-users-found'), [], false, true, site, supervisorForm.supervisorUser || undefined, (user) => `${user.lastName} ${user.firstName}`)
    }, [site, t, supervisorForm.supervisorUser])

    const superviseAllSites = useWatch({
        control,
        name: 'superviseAllSites',
    })

    return (
        // @ts-ignore
        <FormProvider clearErrors={clearErrors}>
            <ErrorAlert errorKey={typeof serverError === 'string' ? serverError : undefined} showAlert={!!serverError} />
            <FormContainer onSubmit={handleSubmit(submit, onError)}>
                {
                    supervisorForm.supervisorUser ? <></> : <FormGroup label={t('supervisors.supervisor')} fullWidth={true}>
                        <FormRow>
                            <FormFieldBox>
                                <TextInput readonly={false} label={t('general.user')} name={'supervisorUser'}
                                           control={control} required={true} autoCompleteOptions={supervisorOptionParams}/>
                            </FormFieldBox>
                        </FormRow>
                    </FormGroup>
                }

                {<FormGroup label={supervisorForm.supervisorUser? '' : t('supervisors.supervised-sites')} fullWidth={true}>
                    <FormRow>
                        <FormFieldBox>
                            <FormCheckbox
                                label={t('supervisors.all-sites')}
                                name={'superviseAllSites'}
                                control={control}
                                setValue={setValue}
                                getFieldState={getFieldState}
                            />
                        </FormFieldBox>
                    </FormRow>
                    {superviseAllSites || !siteOptions || siteOptions.length === 0 ? [] :
                        <FormRow>
                            <FormFieldBox >
                                <TextInput
                                    readonly={false}
                                    label={t('supervisors.select-supervised-sites')}
                                    name={'supervisedSiteIds'}
                                    control={control}
                                    required={true}
                                    options={siteOptions}
                                    isCheckbox={true}

                                />
                            </FormFieldBox>
                        </FormRow>}
                </FormGroup>}
                {buildFormButtons(isSubmitting)}
            </FormContainer>
        </FormProvider>
    )
}

export default AmccSupervisorFormScreen
